import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {combineLatest, firstValueFrom} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {SchemeResultView, SchemeService} from '../openapi-core';
import {CommonService} from './common.service';
import {HealthStateService} from './health-state.service';
import {SessionMonitorService} from './session-monitor.service';
import {SessionService} from './session.service';
import {SetupStateService} from './setup-state.service';

/** Initializes a selected scheme before entering an authenticated Backoffice route. */
@Injectable({providedIn: 'root'})
export class SessionBootstrapService {
  constructor(
    private router: Router,
    private schemeService: SchemeService,
    private sessionService: SessionService,
    private commonService: CommonService,
    private sessionMonitorService: SessionMonitorService,
    private healthStateService: HealthStateService,
    private setupStateService: SetupStateService,
  ) {}

  async initialize(scheme?: SchemeResultView): Promise<void> {
    // /load is the one place that rehydrates a workspace. Do not let callers
    // continue rendering from the previously cached plan while that request is
    // in flight.
    this.sessionService.isSessionReady = false;

    const selectedScheme = scheme ?? await firstValueFrom(this.schemeService.apiSchemeIdGet(this.sessionService.schemeId));
    this.sessionService.scheme = selectedScheme;
    this.clearLoadingWorkspace();

    this.sessionMonitorService.startMonitoring();
    this.healthStateService.startBackgroundSync();
    this.setupStateService.startBackgroundSync();

    await firstValueFrom(combineLatest([
      this.healthStateService.initialCheckCompleted$,
      this.setupStateService.initialCheckCompleted$
    ]).pipe(filter(([healthReady, setupReady]) => healthReady && setupReady), take(1)));
    this.sessionService.isSessionReady = true;
  }

  redirectToDestination(preserveQueryParams = true, replaceUrl = false): Promise<boolean> {
    const destination = sessionStorage.getItem('destination');
    if (destination) {
      sessionStorage.removeItem('destination');
      return replaceUrl
        ? this.router.navigateByUrl(destination, {replaceUrl: true})
        : this.router.navigateByUrl(destination);
    }

    if (preserveQueryParams) {
      return replaceUrl
        ? this.router.navigate(['/general/dashboard'], {queryParamsHandling: 'merge', replaceUrl: true})
        : this.router.navigate(['/general/dashboard'], {queryParamsHandling: 'merge'});
    }

    return replaceUrl
      ? this.router.navigate(['/general/dashboard'], {replaceUrl: true})
      : this.router.navigate(['/general/dashboard']);
  }

  private clearLoadingWorkspace(): void {
    try {
      sessionStorage.removeItem('loadingSchemeId');
      sessionStorage.removeItem('loadingSchemeImageUrl');
      sessionStorage.removeItem('loadingSchemeName');
    } catch {
      // Session bootstrap remains valid when session storage is unavailable.
    }
  }
}
